import React from 'react';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { Container } from '@cp/ui/components';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { Stack } from '@cp/ds/src/components/stack';
import { Button } from '@cp/ds/src/components/button';
import { ArrowRightIcon } from '@cp/ds/src/icons';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds/src/theme';
import { useClientWebRouteProps, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { CatalogItemListFilter } from '@common/model/catalog/item/list.filter';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { useCurrentCity } from '@cp/entities/location';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useMediaQuery } from '@mui/material';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Typography } from '@cp/ds/src/components/typography';
import { coreCareerApi } from '@cp/entities/career';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useRouter } from '@cp/shared/router';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { useH1 } from 'src/app/shared/hooks/useH1';
import { H1 } from 'src/app/shared/components/h1';
import { CatalogSpecializationMenu } from 'src/widgets/CatalogSpecializationMenu';
import { CatalogClientsOnlineTotalCounter } from 'src/widgets/CatalogClientsOnlineTotalCounter';
import { CatalogList, FAQ, ResumeCardWidget, VacancyCardWidget, useCatalogBreadcrumbs } from 'src/widgets/catalog';
import { CatalogFeedbacks } from 'src/widgets/catalog/feedback/list';
import { CatalogBannerSearch } from 'src/entities/catalog/banner';
import { useClientLogin } from 'src/features/client/current/login';
import { CatalogSpecializationTagCloud } from 'src/widgets/CatalogSpecializationTagCloud';
import { CatalogCareerList } from 'src/widgets/CatalogCareerList';
import { useAuthInterceptor } from 'src/features/auth';
import { CatalogItemView, useActiveCatalogItemId, useCloseCatalogItem } from 'src/features/catalogItem/view';
import { CatalogItemActions } from 'src/widgets/catalog/item/actions';
import { RoutedLink } from 'src/shared/ui/link';
import { CatalogViewProps } from 'src/app/routes/catalog/types';
import {
  CATALOG_DESKTOP_COLUMN_SPACING,
  CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
  CATALOG_DESKTOP_CONTAINER_MAX_WIDTH,
  CATALOG_MOBILE_COLUMN_SPACING,
} from '@youhelp/client.web/app/routes/catalog/const';
import { calcDesktopItemListStyles, calcMobileItemListStyleParams } from '@youhelp/client.web/app/routes/catalog/utils/calcItemListStyles';
import { buildSubcatalogInversedListRequest, buildSubcatalogListRequest } from '@youhelp/client.web/app/routes/catalog/lib';

const LIST_MAX_WIDTH = 820;
const VACANCY_LIST_CONTAINER_SX: SxProps = { maxWidth: LIST_MAX_WIDTH, mx: 'auto' };

export const SubcatalogPage: React.FC<CatalogViewProps> = () => {
  const router = useRouter();
  const pathGenerator = usePathGenerator();
  const pageProps = useClientWebRouteProps<ClientWebPage.Catalog>();
  const { currentCity } = useCurrentCity();
  const { careerId, catalogType /*, filter */ } = pageProps;
  const isResumeCatalogType = catalogType === CatalogItemType.Resume;
  const filter: CatalogItemListFilter | undefined = undefined;
  const loggedIn = useIsLoggedIn();
  const login = useClientLogin();
  const h1RenderConfig = useH1();
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const interceptAuth = useAuthInterceptor();
  const { containerSx: resumeListContainerSx, cardWidthAutoUntilBreakpoint: resumeCardWidthAutoUntilBreakpoint } = React.useMemo(
    () => calcDesktopItemListStyles(theme, { widthModifierForMainBlock: 0 }),
    [theme],
  );
  const { screenWidthMaxForSingleColumn, columnSpacing } = React.useMemo(() => calcMobileItemListStyleParams(theme), [theme]);
  const isResumeCardWidthFull = useMediaQuery(theme.breakpoints.down(resumeCardWidthAutoUntilBreakpoint));
  const containerMaxWidth = isMobile ? undefined : CATALOG_DESKTOP_CONTAINER_MAX_WIDTH;

  const { listRequest, inversedListRequest } = React.useMemo<{
    listRequest: CatalogItemListRequest;
    inversedListRequest: CatalogItemListRequest;
  }>(
    () => ({
      listRequest: buildSubcatalogListRequest({ pageProps, cityId: currentCity.id }),
      inversedListRequest: buildSubcatalogInversedListRequest({ pageProps, cityId: currentCity.id }),
    }),
    [currentCity.id, pageProps],
  );

  const handleSearch = interceptAuth(() =>
    router.navigate(pathGenerator({ page: ClientWebPage.Catalog, pageProps: { careerId, catalogType }, queryParams: { filter } })),
  );
  const catalogItemId = useActiveCatalogItemId();
  const handleItemDetailsClose = useCloseCatalogItem();
  const { data: activeItem } = coreCatalogItemApi.endpoints.getById.useQuery(
    { id: catalogItemId as NonNullable<typeof catalogItemId> },
    { skip: !catalogItemId },
  );
  const breadcrumbs = useCatalogBreadcrumbs();

  const renderSection = ({
    title,
    body,
    containerProps,
  }: {
    title: React.ReactNode;
    body: React.ReactNode;
    containerProps?: BoxProps;
  }): React.ReactNode => {
    return (
      <Box {...containerProps} sx={mergeSx(containerProps?.sx, { mt: { xs: 5, [MOBILE_BREAKPOINT_THRESHOLD]: 10 } })}>
        <Typography component="h2" sx={{ mb: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 }, textAlign: 'center' }} variant="h2">
          {title}
        </Typography>
        {body}
      </Box>
    );
  };

  return (
    <>
      <Box component="main" sx={{ backgroundColor: 'background.paper', pb: 15 }}>
        <Container sx={{ maxWidth: containerMaxWidth }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, my: 1.5 }}>
            <Breadcrumbs LinkComponent={RoutedLink} items={breadcrumbs} />
          </Box>
          <H1
            config={h1RenderConfig}
            itemProps={{ bold: { variant: 'h1' } }}
            rootProps={{
              sx: { mt: 4, mb: 0, textAlign: 'center' },
              variant: 'h1',
            }}
          />

          <Stack
            direction={{ xs: 'column', [MOBILE_BREAKPOINT_THRESHOLD]: 'row' }}
            spacing={{ xs: 1, [MOBILE_BREAKPOINT_THRESHOLD]: 4 }}
            sx={{ py: { xs: 1.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 }, justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              color="pink"
              iconRight={<ArrowRightIcon />}
              onClick={handleSearch}
              size={isMobile ? 'small' : 'normal'}
              variant="primary"
            >
              Начать поиск
            </Button>
            <CatalogClientsOnlineTotalCounter careerId={careerId} filter={filter} itemType={catalogType} />
          </Stack>

          <CatalogSpecializationMenu careerId={careerId} itemType={catalogType} />

          {renderSection({
            title: `${isResumeCatalogType ? 'Специалисты' : 'Вакансии'} в ${currentCity.translation.namePrepositional}`,
            body: (
              <Box
                sx={mergeSx(isResumeCatalogType ? resumeListContainerSx : VACANCY_LIST_CONTAINER_SX, {
                  mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 },
                })}
              >
                <CatalogList
                  CatalogItem={isResumeCatalogType ? ResumeCardWidget : VacancyCardWidget}
                  itemSpacing={CATALOG_MOBILE_COLUMN_SPACING}
                  itemWidth={
                    isResumeCardWidthFull || !isResumeCatalogType
                      ? `calc((100% - ${columnSpacing}px)/2)`
                      : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED
                  }
                  request={listRequest}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            ),
          })}

          {isResumeCatalogType &&
            renderSection({
              // eslint-disable-next-line max-len
              title: `Отзывы о наших ${careerDictionary?.[careerId].translation.namePluralPrepositional} в ${currentCity.translation.namePrepositional}`,
              body: <CatalogFeedbacks careerId={careerId} catalogType={catalogType} />,
            })}

          {renderSection({
            title: isResumeCatalogType ? 'Прямо сейчас ищут' : 'Наши специалисты',
            body: (
              <Box
                sx={mergeSx(!isResumeCatalogType ? resumeListContainerSx : VACANCY_LIST_CONTAINER_SX, {
                  mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 },
                })}
              >
                <CatalogList
                  CatalogItem={isResumeCatalogType ? ResumeCardWidget : VacancyCardWidget}
                  itemSpacing={CATALOG_DESKTOP_COLUMN_SPACING}
                  itemWidth={isResumeCardWidthFull || isResumeCatalogType ? '100%' : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED}
                  request={inversedListRequest}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            ),
          })}

          {!loggedIn && (
            <CatalogBannerSearch
              careerDict={careerDictionary}
              careerId={careerId}
              itemType={catalogType}
              onClick={login}
              sx={{ mt: { xs: 5, [MOBILE_BREAKPOINT_THRESHOLD]: 10 }, borderRadius: '24px', overflow: 'hidden' }}
            />
          )}

          {isResumeCatalogType &&
            renderSection({
              title: 'Часто задаваемые вопросы',
              /* eslint-disable-next-line react/jsx-pascal-case */
              body: <FAQ listRequest={listRequest} />,
              containerProps: { sx: { maxWidth: LIST_MAX_WIDTH, mx: 'auto' } },
            })}

          {renderSection({
            title: 'Похожие страницы',
            body: (
              <CatalogSpecializationTagCloud
                careerId={careerId}
                itemType={catalogType}
                maxVisibleItemsCountByDefault={Number.MAX_SAFE_INTEGER}
                sx={{ border: 'none', p: 0, backgroundColor: 'transparent', justifyContent: 'center' }}
                title={false}
              />
            ),
          })}

          {renderSection({
            title: 'Смотрите также',
            body: <CatalogCareerList itemType={catalogType} />,
          })}
        </Container>
      </Box>
      {/* Preview catalog item */}
      <CatalogItemView
        buttons={<CatalogItemActions id={catalogItemId} type={activeItem?.type} />}
        id={catalogItemId ?? null}
        onClose={handleItemDetailsClose}
      />
    </>
  );
};
